import { Box, HStack, Spinner, Text, useColorModeValue } from "@chakra-ui/react";
import { Line } from "react-chartjs-2";
import {
  IndexName,
  PRODUCT_LABELS,
  defaultDisplayRange,
  isScore,
  satelliteColor,
} from "../../utils/satelliteProducts";
import { SatelliteStatPoint, validSeriesPoints } from "../../utils/satelliteTimeSeries";

interface Props {
  indexName: IndexName;
  points: SatelliteStatPoint[] | undefined;
  selectedId: number | null;
  onSelect?: (satelliteDataId: number) => void;
  loading?: boolean;
  height?: number | string;
}

interface MarkerChart {
  ctx: CanvasRenderingContext2D;
  chartArea: { left: number; right: number; top: number; bottom: number };
  scales: Record<string, { getPixelForValue: (value: unknown) => number }>;
}

interface MarkerOptions {
  time?: number | null;
  label?: string;
  color?: string;
}

// Vertical line marking the acquisition currently shown on the map.
const selectedDateMarker = {
  id: "selected-date-marker",
  afterDatasetsDraw(chart: MarkerChart, _easing: unknown, options: MarkerOptions) {
    if (options?.time == null) return;
    const scale = chart.scales["x-axis-0"];
    if (!scale) return;
    const { ctx, chartArea } = chart;
    const x = scale.getPixelForValue(new Date(options.time));
    if (!Number.isFinite(x) || x < chartArea.left - 1 || x > chartArea.right + 1) return;
    const color = options.color ?? "#3182CE";
    ctx.save();
    ctx.strokeStyle = color;
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(x, chartArea.top);
    ctx.lineTo(x, chartArea.bottom);
    ctx.stroke();
    if (options.label) {
      ctx.font = "bold 10px sans-serif";
      const width = ctx.measureText(options.label).width + 8;
      const left = Math.min(Math.max(x - width / 2, chartArea.left), chartArea.right - width);
      ctx.fillStyle = color;
      ctx.fillRect(left, chartArea.top, width, 14);
      ctx.fillStyle = "#fff";
      ctx.textBaseline = "middle";
      ctx.fillText(options.label, left + 4, chartArea.top + 7);
    }
    ctx.restore();
  },
};

const utcDay = (iso: string, withYear = true) =>
  new Date(iso).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    ...(withYear ? { year: "numeric" } : {}),
    timeZone: "UTC",
  });

const SatelliteTimeSeriesChart = ({
  indexName,
  points,
  selectedId,
  onSelect,
  loading = false,
  height = 180,
}: Props) => {
  const textColor = useColorModeValue("#4A5568", "#CBD5E0");
  const gridColor = useColorModeValue("rgba(0,0,0,0.06)", "rgba(255,255,255,0.08)");
  const lineColor = useColorModeValue("#2D3748", "#E2E8F0");
  const markerColor = useColorModeValue("#3182CE", "#63B3ED");
  const bandColor = useColorModeValue("rgba(113,128,150,0.18)", "rgba(203,213,224,0.15)");

  const series = validSeriesPoints(points);
  const score = isScore(indexName);
  const format = (value: number) => (score ? `${Math.round(value)}` : value.toFixed(2));

  if (loading && !series.length) {
    return (
      <HStack h={height} justify="center" color="gray.500" fontSize="sm">
        <Spinner size="sm" />
        <Text>Loading field average…</Text>
      </HStack>
    );
  }
  if (!series.length) {
    return (
      <Text fontSize="sm" color="gray.500">
        No field averages are available for {PRODUCT_LABELS[indexName]} yet.
      </Text>
    );
  }

  const selected = series.find((point) => point.satellite_data_id === selectedId) ?? null;
  const [colorMin, colorMax] = defaultDisplayRange(indexName);
  const xy = (key: "mean" | "p10" | "p90") =>
    series.map((point) => ({ x: new Date(point.acquisition_date), y: point[key] }));
  const pointColors = series.map((point) => {
    const [r, g, b] = satelliteColor(indexName, point.mean as number, colorMin, colorMax);
    return `rgb(${r}, ${g}, ${b})`;
  });
  const isSelected = (index: number) => series[index].satellite_data_id === selectedId;

  return (
    <Box>
      <Box h={height} position="relative" minW={0}>
        {/* react-chartjs-2 v2 merges options on update, so stale axis limits survive an index change without a remount. */}
        <Line
          key={indexName}
          plugins={[selectedDateMarker]}
          data={{
            datasets: [
              {
                label: "10th percentile",
                data: xy("p10"),
                borderWidth: 0,
                borderColor: "transparent",
                pointRadius: 0,
                pointHitRadius: 0,
                fill: false,
                lineTension: 0,
              },
              {
                label: "90th percentile",
                data: xy("p90"),
                borderWidth: 0,
                borderColor: "transparent",
                pointRadius: 0,
                pointHitRadius: 0,
                backgroundColor: bandColor,
                fill: "-1",
                lineTension: 0,
              },
              {
                label: `${PRODUCT_LABELS[indexName]} field average`,
                data: xy("mean"),
                borderColor: lineColor,
                borderWidth: 1.5,
                fill: false,
                lineTension: 0,
                pointBackgroundColor: pointColors,
                pointBorderColor: series.map((_, i) => (isSelected(i) ? markerColor : lineColor)),
                pointBorderWidth: series.map((_, i) => (isSelected(i) ? 3 : 1)),
                pointRadius: series.map((_, i) => (isSelected(i) ? 6 : 3)),
                pointHoverRadius: 6,
                pointHitRadius: 8,
              },
            ],
          }}
          options={{
            maintainAspectRatio: false,
            animation: { duration: 0 },
            legend: { display: false },
            layout: { padding: { top: 4, right: 4 } },
            hover: { mode: "nearest", intersect: false, axis: "x" },
            onHover: (event: any, elements: any[]) => {
              const target = event?.target as HTMLElement | undefined;
              if (target && onSelect) target.style.cursor = elements.length ? "pointer" : "default";
            },
            onClick: (_event: unknown, elements: Array<{ _index: number }>) => {
              const point = elements.length ? series[elements[0]._index] : undefined;
              if (point && onSelect) onSelect(point.satellite_data_id);
            },
            tooltips: {
              mode: "nearest",
              intersect: false,
              axis: "x",
              filter: (item: { datasetIndex: number }) => item.datasetIndex === 2,
              callbacks: {
                title: (items: Array<{ index: number }>) =>
                  items.length ? utcDay(series[items[0].index].acquisition_date) : "",
                label: (item: { index: number }) => {
                  const point = series[item.index];
                  return `Average: ${format(point.mean as number)}`;
                },
                afterLabel: (item: { index: number }) => {
                  const point = series[item.index];
                  const lines = [];
                  if (point.p10 !== null && point.p90 !== null) {
                    lines.push(`Most of field: ${format(point.p10)} – ${format(point.p90)}`);
                  }
                  if (point.coverage !== null) {
                    lines.push(`Cloud free: ${Math.round(point.coverage * 100)}%`);
                  }
                  return lines;
                },
              },
            },
            scales: {
              xAxes: [
                {
                  type: "time",
                  distribution: "linear",
                  time: { minUnit: "day", tooltipFormat: "ll" },
                  ticks: { maxTicksLimit: 6, maxRotation: 0, fontSize: 10, fontColor: textColor },
                  gridLines: { display: false },
                },
              ],
              yAxes: [
                {
                  ticks: {
                    fontSize: 10,
                    fontColor: textColor,
                    maxTicksLimit: 5,
                    ...(score ? { min: 0, max: 100 } : {}),
                    callback: (value: number | string) => format(Number(value)),
                  },
                  gridLines: { color: gridColor, drawBorder: false },
                },
              ],
            },
            plugins: {
              "selected-date-marker": {
                time: selected ? Date.parse(selected.acquisition_date) : null,
                label: selected ? utcDay(selected.acquisition_date, false) : undefined,
                color: markerColor,
              },
            },
          } as any}
        />
      </Box>
      <Text fontSize="xs" color="gray.500" mt={1}>
        Field average per image. Shaded band: middle 80% of the field. Click a point to show that date.
      </Text>
    </Box>
  );
};

export default SatelliteTimeSeriesChart;
